import { inject, Injectable, OnDestroy } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { BehaviorSubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { LocalStorage, SessionStorage } from "@dffedb/util";

import { AktuelForsyningIndstillingerObserver } from "../forsyning-indstillinger";
import { AppRatingService } from "./app-rating.service";

@Injectable({ providedIn: "root" })
export class AppRatingInteractionTrackerService implements OnDestroy {
    private readonly promptDelayMs = 90000; // 90 seconds
    private readonly sessionDismissedKey = "appReview.bannerDismissedSession";
    private readonly neverShowAgainKey = "appReview.bannerNeverShowAgain";

    private readonly showBannerSubject = new BehaviorSubject<boolean>(false);
    public readonly showBanner$ = this.showBannerSubject.asObservable();

    private started = false;
    private timerArmed = false;
    private promptTimerId: ReturnType<typeof setTimeout> | null = null;
    private readonly destroy$ = new Subject<void>();

    private readonly appRatingService = inject(AppRatingService);
    private readonly sessionStorage = inject(SessionStorage);
    private readonly localStorage = inject(LocalStorage);
    private readonly aktuelForsyningIndstillinger = inject(AktuelForsyningIndstillingerObserver);

    public start(): void {
        if (this.started) {
            return;
        }

        if (!Capacitor.isNativePlatform()) {
            return;
        }

        if (this.isDismissedForSession() || this.isNeverShowAgain()) {
            return;
        }

        this.started = true;

        this.aktuelForsyningIndstillinger.isPilot.pipe(takeUntil(this.destroy$)).subscribe((isPilot) => {
            if (isPilot) {
                this.armPromptTimer();
            } else {
                this.clearPromptTimer();
                this.showBannerSubject.next(false);
            }
        });
    }

    public async confirmAndRequestReview(): Promise<void> {
        const didRequestReview = await this.appRatingService.requestReviewFromUserAction();
        if (didRequestReview) {
            this.dismissForSession();
        } else {
            this.showBannerSubject.next(false);
        }
    }

    public dismissForSession(): void {
        this.sessionStorage.setItem(this.sessionDismissedKey, "1");
        this.showBannerSubject.next(false);
    }

    public dismissForever(): void {
        this.localStorage.setItem(this.neverShowAgainKey, "1");
        this.dismissForSession();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearPromptTimer();
    }

    private armPromptTimer(): void {
        if (this.timerArmed || this.isDismissedForSession() || this.isNeverShowAgain()) {
            return;
        }

        this.timerArmed = true;
        this.promptTimerId = setTimeout(() => {
            const shouldShowBanner = this.appRatingService.markPromptShownIfEligible();
            if (shouldShowBanner && !this.isDismissedForSession() && !this.isNeverShowAgain()) {
                this.showBannerSubject.next(true);
            }
        }, this.promptDelayMs);
    }

    private clearPromptTimer(): void {
        if (this.promptTimerId) {
            clearTimeout(this.promptTimerId);
            this.promptTimerId = null;
        }
        this.timerArmed = false;
    }

    private isDismissedForSession(): boolean {
        return this.sessionStorage.getItem(this.sessionDismissedKey) === "1";
    }

    private isNeverShowAgain(): boolean {
        return this.localStorage.getItem(this.neverShowAgainKey) === "1";
    }
}
